import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { fetchCategories, fetchHome } from '../../services/catalog.js';
import { keys } from '../../services/queryClient.js';
import { fetchLivePromotion } from '../../services/adminPromos.js';
import { fetchPlan } from '../../services/subscriptions.js';
import BookTile from '../../components/catalog/BookTile.jsx';
import CloudShaderGenres from '../../components/home/CloudShaderGenres.jsx';
import HeroShowcaseCarousel from '../../components/home/HeroShowcaseCarousel.jsx';
import MeliusBookHero from '../../components/home/MeliusBookHero.jsx';

/**
 * `href`/`cta` are optional: the subject band lists everything it has, so there is
 * no "see all" for it to point at.
 */
function SectionHead({ eyebrow, title, blurb, href, cta }) {
  return (
    <div className="rule-b flex flex-wrap items-end justify-between gap-6 pb-6">
      <div>
        <p className="label text-stamp">{eyebrow}</p>
        <h2 className="text-display-md mt-2">{title}</h2>
        {blurb && <p className="text-ink-80 mt-3 max-w-[48ch] text-sm leading-relaxed">{blurb}</p>}
      </div>
      {href && cta && (
        <Link to={href} className="label text-ink hover:text-stamp shrink-0">
          {cta} &rarr;
        </Link>
      )}
    </div>
  );
}

/**
 * One row of the subject index: the name, then a way onto each shelf.
 */
function SubjectRow({ subject }) {
  const showNew = subject.hasNew !== false;
  const showUsed = subject.hasSecondhand !== false;

  return (
    <li className="rule-b flex items-baseline justify-between gap-4 py-3.5 pr-8 sm:pr-14">
      <span className="font-display text-lg tracking-tight">{subject.name}</span>

      <span className="flex shrink-0 items-baseline gap-3">
        {showNew && (
          <Link
            to={`/new?category=${subject.slug}`}
            aria-label={`${subject.name}, new copies`}
            className="label text-ink-60 hover:text-stamp no-underline transition-colors duration-150"
          >
            New
          </Link>
        )}
        {showNew && showUsed && (
          <span aria-hidden="true" className="label text-ink-40">
            /
          </span>
        )}
        {showUsed && (
          <Link
            to={`/secondhand?category=${subject.slug}`}
            aria-label={`${subject.name}, secondhand copies`}
            className="label text-ink-60 hover:text-stamp no-underline transition-colors duration-150"
          >
            Used
          </Link>
        )}
      </span>
    </li>
  );
}

export default function HomePage() {
  const { data, isLoading } = useQuery({
    queryKey: ['home'],
    queryFn: fetchHome,
  });

  const {
    data: taxonomy,
    isLoading: loadingSubjects,
    isError: subjectsFailed,
  } = useQuery({
    queryKey: keys.taxonomy.categories,
    queryFn: fetchCategories,
    staleTime: 5 * 60_000,
  });

  const { data: promoData } = useQuery({
    queryKey: ['promotions', 'live'],
    queryFn: fetchLivePromotion,
  });

  const { data: planData } = useQuery({
    queryKey: ['subscription', 'plan'],
    queryFn: fetchPlan,
  });

  const newReleases = data?.newReleases || [];
  const secondhandFinds = data?.secondhandFinds || [];
  const subjects = taxonomy?.items || [];
  const livePromotion = promoData?.promotion;
  const isPlanActive = planData?.isActive !== false;

  return (
    <>
      {/* Event Promotion Top Banner (rendered only when an admin enables a live promotion) */}
      {livePromotion && (
        <aside aria-label="Active Event Promotion" className="bg-paper-deep border-b border-rule py-2.5 px-4 text-center">
          <Link
            to="/book-fair"
            className="text-xs text-ink hover:text-stamp-ink font-sans inline-flex items-center gap-2 no-underline transition-colors"
          >
            <span className="label bg-stamp-ink text-paper text-[10px] px-1.5 py-0.5 uppercase tracking-wider">
              Event Live
            </span>
            <span className="font-medium">{livePromotion.name}:</span>
            <span className="text-ink-80">
              {livePromotion.publicBlurb || 'Special membership rewards active for book fair attendees.'}
            </span>
            <span className="text-stamp-ink font-mono font-bold ml-1">&rarr;</span>
          </Link>
        </aside>
      )}

      {/* --- Melius-Style Interactive Hero: Popular & Bestselling Books from Sri Lanka & Worldwide --- */}
      <MeliusBookHero />


      {/* --- Books & Merchandise Showcase Carousel (matching reference layout) --- */}
      <HeroShowcaseCarousel items={newReleases} />

      {/* --- Live Sky & Clouds Shader with Contrasting Book Genre Circles --- */}
      <CloudShaderGenres categories={subjects} />

      {/* --- Catalog one: publisher books ---------------------------------- */}
      <section className="page-gutter mx-auto max-w-(--container-page) pt-12 pb-16 sm:pt-16 sm:pb-24">
        <SectionHead
          eyebrow="Section 01 &middot; Just In"
          title="New Releases"
          blurb="Fresh from the publishers, in the condition they left the printer."
          href="/new"
          cta="All new releases"
        />
        {isLoading ? (
          <div className="py-16 text-center">
            <p className="label text-ink-60 animate-pulse">Loading new arrivals&hellip;</p>
          </div>
        ) : newReleases.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-ink-60 text-sm">New titles arriving shortly.</p>
          </div>
        ) : (
          <div className="mt-8 sm:mt-12 grid grid-cols-2 gap-x-4 gap-y-8 sm:gap-x-6 sm:gap-y-12 sm:grid-cols-3 md:grid-cols-4 md:gap-x-8">
            {newReleases.map((book, i) => (
              <BookTile key={book.slug || book.id} book={book} kind="publisher" index={i} />
            ))}
          </div>
        )}
      </section>

      {/* --- Catalog two: secondhand -------------------------------------- */}
      <section className="page-gutter mx-auto max-w-(--container-page) pt-16 pb-16 sm:pt-24 sm:pb-24">
        <SectionHead
          eyebrow="Section 02 &middot; Single copies only"
          title="Secondhand Finds"
          blurb="Each copy inspected and graded by hand. When it is gone, it is gone."
          href="/secondhand"
          cta="All secondhand"
        />
        {isLoading ? (
          <div className="py-16 text-center">
            <p className="label text-ink-60 animate-pulse">Checking secondhand shelves&hellip;</p>
          </div>
        ) : secondhandFinds.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-ink-60 text-sm">Secondhand copies being graded.</p>
          </div>
        ) : (
          <div className="mt-8 sm:mt-12 grid grid-cols-2 gap-x-4 gap-y-8 sm:gap-x-6 sm:gap-y-12 sm:grid-cols-3 md:grid-cols-4 md:gap-x-8">
            {secondhandFinds.map((book, i) => (
              <BookTile key={book.slug || book.id} book={book} kind="secondhand" index={i + 1} />
            ))}
          </div>
        )}
      </section>

      {/* --- The subject index -------------------------------------------- */}
      {!subjectsFailed && (
        <section className="bg-paper-deep rule-t rule-b">
          <div className="page-gutter mx-auto max-w-(--container-page) py-14 sm:py-20">
            <SectionHead
              eyebrow="Taxonomy"
              title="Browse the Shelves"
              blurb="One subject, both shelves — the new copies and the secondhand ones, side by side."
            />
            {loadingSubjects ? (
              <div className="py-12 text-center">
                <p className="label text-ink-60 animate-pulse">Reading the index&hellip;</p>
              </div>
            ) : subjects.length === 0 ? (
              <div className="py-12 text-center">
                <p className="text-ink-60 text-sm">Subjects are being shelved.</p>
              </div>
            ) : (
              <ul className="mt-8 sm:mt-10 grid list-none gap-x-8 sm:gap-x-12 p-0 sm:grid-cols-2 lg:grid-cols-3">
                {subjects.map((subject) => (
                  <SubjectRow key={subject.slug} subject={subject} />
                ))}
              </ul>
            )}
          </div>
        </section>
      )}

      {/* --- Subscription: The Architectural Interlude (Brand Navy Plate) --- */}
      <section className="page-gutter mx-auto my-14 sm:my-24 max-w-(--container-page)">
        <div className="bg-slab text-slab-ink border border-slab-rule grid gap-6 p-6 sm:gap-8 sm:p-10 md:grid-cols-[1.3fr_1fr] md:items-end md:p-16">
          <div>
            <div className="flex items-center gap-3">
              <p className="label text-slab-stamp font-medium">The Chapters&amp;Co Reading Circle</p>
              {!isPlanActive && (
                <span className="label bg-slab-ink/60 text-slab-ink-80 border border-slab-rule px-2 py-0.5 text-[10px] uppercase tracking-wider">
                  Enrollment Paused
                </span>
              )}
            </div>
            <h2 className="text-display-md mt-3 sm:mt-4">
              {isPlanActive
                ? 'A subscription for readers who never stop.'
                : 'Membership enrollment is temporarily paused.'}
            </h2>
            <p className="text-read text-slab-ink-80 font-display mt-4 sm:mt-5 max-w-[44ch]">
              {isPlanActive
                ? 'A standing discount on every order and delivery on us. Configured by our shop, not by an algorithm.'
                : 'We are currently not accepting new subscribers. Existing members continue to enjoy full benefits on all orders.'}
            </p>
          </div>
          <Link
            to="/subscription"
            className="label bg-slab-ink text-slab hover:bg-stamp hover:text-paper justify-self-start w-full sm:w-auto text-center px-7 py-3.5 no-underline transition-colors duration-200"
          >
            {isPlanActive ? 'Explore the subscription \u2192' : 'View membership status \u2192'}
          </Link>
        </div>
      </section>
    </>
  );
}
